<!--
  AskListTools: search and three filters for the ranked list on Today.

  Usage, in AskHome directly under the Sort segmented control in the list header:

    <AskListTools v-if="!askView.legacy" />

  No props, no events: it reads and writes the ask store (search, legs, odds,
  book), and the list re-asks when the request changes.
-->
<template>
  <div class="list-tools">
    <!-- SEARCH: a filled field, not a bordered box. Typing settles for 250ms
         before the list re-asks, so a name is one search, not six. -->
    <div class="search" role="search">
      <label :for="inputId" class="visually-hidden">Search the multis</label>
      <Glyph name="search" class="search-glyph" />
      <input
        :id="inputId"
        v-model="text"
        class="search-input"
        type="search"
        inputmode="search"
        enterkeyhint="search"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        maxlength="40"
        placeholder="Search teams, runners, venues"
        @input="onInput"
        @keydown.enter.prevent="flush"
      />
      <button
        v-if="text"
        type="button"
        class="search-clear"
        aria-label="Clear the search"
        @click="clearSearch"
      ><Glyph name="close" /></button>
    </div>

    <!-- FILTERS: a pill says its value and is filled when it is saying
         something; a tap opens its choices. -->
    <div class="pills" role="group" aria-label="Filter the multis">
      <button
        v-for="p in pills"
        :key="p.id"
        type="button"
        class="pill"
        :class="{ 'pill--set': p.set }"
        :data-pill="p.id"
        aria-haspopup="dialog"
        @click="open(p.id)"
      >{{ p.label }}</button>
    </div>

    <IonActionSheet
      :is-open="!!sheet"
      :header="sheetHeader"
      :buttons="sheetButtons"
      @didDismiss="sheet = null"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount, useId } from 'vue';
import { IonActionSheet } from '@ionic/vue';
import Glyph from './Glyph.vue';
import {
  askSelections, askBooks, askBook, setBook, setLegs, setOdds, setSearch, ANY_BOOK,
} from '../../lib/stores/ask.js';
import { riskProfile } from '../../store.js';
import { bookmakerName } from '../../lib/odds.js';
import {
  LEG_FILTERS, PAY_FILTERS, legsWord, paysWord, isChoice,
} from '../../lib/ask/list-tools.js';

const SEARCH_DEBOUNCE_MS = 250;
/** An action sheet's choices before Clear: more than this is a list, not a sheet. */
const MAX_CHOICES = 6;

const inputId = `list-search-${useId()}`;

// --- search ---------------------------------------------------------------------------
const text = ref(askSelections.search ?? '');
let timer = null;
function flush() {
  clearTimeout(timer);
  timer = null;
  if ((askSelections.search ?? '') !== text.value) setSearch(text.value);
}
function onInput() {
  clearTimeout(timer);
  timer = setTimeout(flush, SEARCH_DEBOUNCE_MS);
}
function clearSearch() {
  text.value = '';
  flush();
}
// Start over, or "Clear the search" from an unsatisfied answer, empties the
// store; the field follows it.
watch(() => askSelections.search, (s) => {
  if (!timer && s !== text.value) text.value = s ?? '';
});
onBeforeUnmount(() => clearTimeout(timer));

// --- the bookmaker --------------------------------------------------------------------
const nameOf = (key, fallback) => {
  const known = bookmakerName(key);
  return known && known !== key ? known : fallback ?? key;
};
const ownKey = computed(() => riskProfile.value?.bookmaker ?? 'sportsbet');
const ownName = computed(() => nameOf(ownKey.value, askBooks.value.find((b) => b.key === ownKey.value)?.name ?? 'Sportsbet'));
const bookLabel = computed(() => {
  const key = askBook.value;
  if (!key) return 'Any bookmaker';
  if (key === ownKey.value) return ownName.value;
  return nameOf(key, askBooks.value.find((b) => b.key === key)?.name);
});
const bookChoices = computed(() => {
  const own = askSelections.book === null || askSelections.book === ownKey.value;
  const others = askBooks.value.filter((b) => b.key !== ownKey.value).slice(0, MAX_CHOICES - 2);
  return [
    { label: 'Any bookmaker', value: ANY_BOOK, on: askSelections.book === ANY_BOOK },
    { label: `${ownName.value} (your book)`, value: null, on: own },
    ...others.map((b) => ({ label: nameOf(b.key, b.name), value: b.key, on: askSelections.book === b.key })),
  ];
});

// --- the pills ------------------------------------------------------------------------
const pills = computed(() => [
  { id: 'legs', label: `Legs: ${legsWord(askSelections.legs) ?? 'Any'}`, set: !!askSelections.legs },
  { id: 'pays', label: `Pays: ${paysWord(askSelections.odds) ?? 'Any'}`, set: !!askSelections.odds },
  { id: 'book', label: bookLabel.value, set: askSelections.book !== ANY_BOOK },
]);

const sheet = ref(null);
function open(id) { sheet.value = id; }

const SHEETS = {
  legs: {
    header: 'Legs',
    choices: () => LEG_FILTERS.map((f) => ({ label: f.label, value: f.value, on: isChoice(f.value, askSelections.legs) })),
    apply: (v) => setLegs(v && typeof v === 'object' ? { ...v } : v),
    clear: () => setLegs(null),
  },
  pays: {
    header: 'Pays',
    choices: () => PAY_FILTERS.map((f) => ({ label: f.label, value: f.value, on: isChoice(f.value, askSelections.odds) })),
    apply: (v) => setOdds(v),
    clear: () => setOdds(null),
  },
  book: {
    header: 'Bookmaker',
    choices: () => bookChoices.value,
    apply: (v) => setBook(v),
    clear: () => setBook(ANY_BOOK),
  },
};

const sheetHeader = computed(() => SHEETS[sheet.value]?.header ?? '');
const sheetButtons = computed(() => {
  const s = SHEETS[sheet.value];
  if (!s) return [];
  return [
    ...s.choices().slice(0, MAX_CHOICES).map((c) => ({
      text: c.label,
      ...(c.on ? { role: 'selected' } : {}),
      handler: () => s.apply(c.value),
    })),
    { text: 'Clear', role: 'destructive', handler: () => s.clear() },
    { text: 'Cancel', role: 'cancel' },
  ];
});

defineExpose({ pills, sheetButtons, open, flush });
</script>

<style scoped>
.list-tools {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2, 8px);
}

/* --- search ---------------------------------------------------------------------- */
.search {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 44px;
  border-radius: 10px;
  background: var(--fill, var(--rule));
  color: var(--carbon-faint);
}
.search:focus-within {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}
.search-glyph {
  margin-left: 10px;
  font-size: 1rem;
}
.search-input {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 44px;
  padding: 0 8px;
  border: 0;
  background: transparent;
  color: var(--carbon);
  font: 400 1.0625rem/1.2 var(--font-body);
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}
.search-input::placeholder {
  color: var(--carbon-faint);
}
.search-input::-webkit-search-cancel-button {
  display: none;
}
.search-clear {
  display: grid;
  place-items: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--carbon-faint);
  font-size: 0.875rem;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.search-clear:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: -2px;
}

/* --- pills ----------------------------------------------------------------------- */
.pills {
  display: flex;
  gap: var(--sp-2, 8px);
  overflow-x: auto;
  scrollbar-width: none;
}
.pills::-webkit-scrollbar {
  display: none;
}
.pill {
  flex: 0 0 auto;
  min-height: 44px;
  padding: 0 14px;
  border: 0;
  border-radius: 999px;
  background: var(--fill, var(--rule));
  color: var(--carbon);
  font: 600 0.875rem/1 var(--font-body);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.pill--set {
  background: var(--accent-soft);
  color: var(--accent);
}
.pill:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
</style>
